import { useState, type MouseEventHandler, type ReactNode } from 'react';

export interface SidebarProps {
  children: ReactNode;
  /** Slide the sidebar in on mobile (adds .sidebar-mobile-expanded). */
  mobileExpanded?: boolean;
  /** Collapse to an icon-only rail on desktop (adds .sidebar-main-resized); unfolds on hover. */
  resized?: boolean;
  className?: string;
}

/** Limitless dark main sidebar (left navigation). */
export function Sidebar({ children, mobileExpanded = false, resized = false, className = '' }: SidebarProps) {
  // While resized, hovering temporarily unfolds the rail to its full width (Limitless behaviour).
  const [unfold, setUnfold] = useState(false);
  const classes = [
    'sidebar sidebar-dark sidebar-main sidebar-expand-lg',
    mobileExpanded ? 'sidebar-mobile-expanded' : '',
    resized ? 'sidebar-main-resized' : '',
    resized && unfold ? 'sidebar-main-unfold' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <div
      className={classes}
      onMouseEnter={resized ? () => setUnfold(true) : undefined}
      onMouseLeave={resized ? () => setUnfold(false) : undefined}
    >
      <div className="sidebar-content" style={{ overflowY: 'auto', maxHeight: '100%' }}>
        {children}
      </div>
    </div>
  );
}

export interface SidebarHeaderProps {
  title?: ReactNode;
  /** Mobile close (x) handler. */
  onClose?: MouseEventHandler<HTMLButtonElement>;
  /** Desktop collapse/expand handler — shows the resize toggle (lg+) when provided. */
  onToggleResize?: MouseEventHandler<HTMLButtonElement>;
}

/** Sidebar header band with a title, the desktop resize toggle, and the mobile close control. */
export function SidebarHeader({ title = 'Navigation', onClose, onToggleResize }: SidebarHeaderProps) {
  return (
    <div className="sidebar-section">
      <div className="sidebar-section-body d-flex justify-content-center">
        <h5 className="sidebar-resize-hide flex-grow-1 my-auto">{title}</h5>
        <div>
          {onToggleResize && (
            <button
              type="button"
              className="btn btn-flat-white btn-icon btn-sm rounded-pill border-transparent d-none d-lg-inline-flex"
              onClick={onToggleResize}
              title="Collapse / expand navigation"
            >
              <i className="ph-arrows-left-right" />
            </button>
          )}
          <button
            type="button"
            className="btn btn-flat-white btn-icon btn-sm rounded-pill border-transparent sidebar-mobile-main-toggle d-lg-none"
            onClick={onClose}
          >
            <i className="ph-x" />
          </button>
        </div>
      </div>
    </div>
  );
}

/** Wraps the main nav list (ul.nav.nav-sidebar) in a sidebar section. */
export function SidebarNav({ children }: { children: ReactNode }) {
  return (
    <div className="sidebar-section">
      <ul className="nav nav-sidebar" data-nav-type="accordion">
        {children}
      </ul>
    </div>
  );
}

/** Uppercase section label between groups of items. */
export function SidebarNavHeader({ label, first = false }: { label: ReactNode; first?: boolean }) {
  return (
    <li className={`nav-item-header ${first ? 'pt-0' : ''}`.trim()}>
      <div className="text-uppercase fs-sm lh-sm opacity-50 sidebar-resize-hide">{label}</div>
      <i className="ph-dots-three sidebar-resize-show" />
    </li>
  );
}

export interface SidebarNavItemProps {
  icon?: string;
  label: ReactNode;
  href?: string;
  /** Anchor target, e.g. "_blank" to open in a new tab (adds rel="noreferrer"). */
  target?: string;
  active?: boolean;
  badge?: ReactNode;
  /** Secondary muted line under the label. */
  subtext?: ReactNode;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  /** Submenu items (SidebarNavItem). When present this becomes a collapsible group. */
  children?: ReactNode;
  /** Open the submenu by default. */
  defaultOpen?: boolean;
}

/** A sidebar nav item — a leaf link, or a collapsible submenu when given children. */
export function SidebarNavItem({
  icon,
  label,
  href = '#',
  target,
  active = false,
  badge,
  subtext,
  onClick,
  children,
  defaultOpen = false,
}: SidebarNavItemProps) {
  const [open, setOpen] = useState(defaultOpen);

  const linkInner = (
    <>
      {icon && <i className={icon} />}
      <span>
        {label}
        {subtext && <span className="d-block fw-normal opacity-50">{subtext}</span>}
      </span>
      {badge != null && (
        <span className="badge bg-primary align-self-center rounded-pill ms-auto">{badge}</span>
      )}
    </>
  );

  if (children) {
    return (
      <li className={`nav-item nav-item-submenu ${open ? 'nav-item-open' : ''}`.trim()}>
        <a
          href="#"
          className={`nav-link ${active ? 'active' : ''}`.trim()}
          onClick={(e) => {
            e.preventDefault();
            setOpen((o) => !o);
          }}
        >
          {linkInner}
        </a>
        <ul className={`nav-group-sub collapse ${open ? 'show' : ''}`.trim()}>{children}</ul>
      </li>
    );
  }

  return (
    <li className="nav-item">
      <a
        href={href}
        target={target}
        rel={target === '_blank' ? 'noreferrer' : undefined}
        className={`nav-link ${active ? 'active' : ''}`.trim()}
        onClick={(e) => {
          if (href === '#') e.preventDefault();
          onClick?.(e);
        }}
      >
        {linkInner}
      </a>
    </li>
  );
}
